import { useMemo, useState, useLayoutEffect, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { useTranslation } from "react-i18next";

import useLayoutStore from "../../store/useLayoutStore";
import useAuthStore from "../../store/useAuthStore";
import useAppStore from "../../store/useAppStore";

import EditDeviceModal from "./EditDeviceModal";


const DevicesCard = ({ device, index }) => {
    const { t } = useTranslation("devices");
    const deviceState = useAppStore(state => state.devicesState[device.client])
    const currentUserId = useAuthStore.getState().userId;
    const userRole = useAuthStore(state => state.userRole);
    const isAdmin = userRole === 'admin';

    const isEmployeeActive = useAppStore(state => {
        const employeeId = device.employee?.userId ?? device.employee;
        if (!employeeId) return false;
        if (employeeId !== currentUserId) return false;

        return state.employeesState?.[employeeId]?.currentActive === true;
    });

    const [isEditing, setIsEditing] = useState(false);

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const cardContainerRef = useRef(null);

    const imageUrl = useMemo(() => {
        const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
        return `${baseImgUrl}/devices/${device.image}`;
    }, [device]);

    const employeeInfo = useMemo(() => {
        const employeeId = device.employee?.userId ?? device.employee;
        if (!employeeId) return null;
        const employeeData = useAppStore.getState().employeesData?.[employeeId];
        if (!employeeData) return null;
        const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
        const currentUserId = useAuthStore.getState().userId;
        const isMyDevice = currentUserId === employeeId &&
            device.device === "S26R2" &&
            deviceState.status === "online" &&
            isEmployeeActive === true;

        return {
            imageUrl: `${baseImgUrl}/employees/${employeeData.image}`,
            fullName: `${employeeData.ime} ${employeeData.prezime}`,
            isMyDevice: isMyDevice
        };
    }, [device, deviceState.status, isEmployeeActive]);

    const controls = useAnimation();

    useLayoutEffect(() => {
        const container = cardContainerRef.current;
        if (!deviceState) return

        const isGroupMoveEntrance = useAppStore
            .getState()
            .deviceGroupMovedClients.includes(device.client);

        if (deviceState.visibility) {
            container.style.display = "flex";
            if (isGroupMoveEntrance) {
                controls.set({ scale: 0.4, opacity: 0 });
                controls.start({
                    scale: 1,
                    opacity: 1,
                    transition: { duration: 0.24, delay: 0.02 * index },
                }).then(() => {
                    useAppStore.getState().clearDeviceGroupMoveAnimation(device.client);
                });
                return;
            }
            controls.start({
                scale: 1,
                opacity: 1,
                transition: { duration: 0.2, delay: 0.02 * index },
            });
        } else {
            controls.start({
                scale: 0.4,
                opacity: 0.4,
                transition: { duration: 0.2 },
            }).then(() => {
                // Nakon animacije, sakrij element
                container.style.display = "none";
            });
        }
    }, [deviceState, device.client, index]);

    return (
        <motion.div
            ref={cardContainerRef}
            className="dv-card-container"
            animate={controls}
        >
            <div className={`dv-card ${isDarkTheme ? 'dark-card' : 'white-card'}`}>
                <div className="dv-card-img-container">
                    <img src={imageUrl} alt={device.device} crossOrigin="use-credentials" className={`${isDarkTheme ? 'dark-shadow' : ''}`} />
                    <AnimatePresence mode="wait">
                        {employeeInfo?.isMyDevice && <DevicePowerSwitcher client={device.client} />}
                    </AnimatePresence>
                </div>
                <div className="dv-card-info-container">
                    <h4>{device.device}</h4>
                    <div className="dv-card-info-div">
                        <div>
                            <span className="dv-label">{t("card.type")}</span>
                            <span className="dv-value">
                                {(t("deviceTypes", { returnObjects: true })?.[device.type]) || device.type}
                            </span>
                        </div>
                        <div>
                            <span className="dv-label">{t("card.room")}</span>
                            <span className="dv-value">{device.room}</span>
                        </div>

                        <DeviceStatusInfo client={device.client} type={device.device} />
                    </div>
                </div>
                <div className="dv-card-tools-container">
                    <WifiFull client={device.client} />
                    {isAdmin && (
                        <img
                            className={`dv-card-edit-tool-btn ${isDarkTheme ? 'dark-filter' : ''}`}
                            src="/cards/Edit.svg"
                            alt="Edit"
                            onClick={() => setIsEditing(true)}
                        />
                    )}
                    {employeeInfo?.imageUrl && (
                        <EmployeeConnected employeeInfo={employeeInfo} />
                    )}
                </div>
            </div>
            <AnimatePresence>
                {isEditing && (
                    <EditDeviceModal
                        key="edit-modal"
                        device={device}
                        onClose={() => setIsEditing(false)}
                    />
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const DevicePowerSwitcher = ({ client }) => {
    const deviceStatePower = useAppStore(state => state.devicesState[client].power)
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const isPowerOn = deviceStatePower === "ON";

    return (
        <>
            <motion.div
                className="dv-card-power-switcher"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{ duration: 0.19, ease: "easeInOut" }}
            >
                <label
                    className="switch"
                    onClick={() => setIsConfirmModalOpen(true)}
                >
                    <input className="checkbox" type="checkbox" checked={isPowerOn} readOnly />
                    <svg
                        className="svg"
                        width="24px"
                        height="24px"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <ellipse
                            className="svg-dot"
                            cx="6"
                            cy="6"
                            rx="2"
                            ry="1"
                            fill="#fff"
                            transform="rotate(-45,6,6)"
                        ></ellipse>
                        <circle
                            className="svg-ring"
                            cx="12"
                            cy="12"
                            r="6"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeDasharray="0 5 27.7 5"
                            strokeDashoffset="0.01"
                            transform="rotate(-90,12,12)"
                        ></circle>
                        <line
                            className="svg-line"
                            x1="12"
                            y1="6"
                            x2="12"
                            y2="15"
                            stroke="#fff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeDasharray="9 9"
                            strokeDashoffset="3"
                        ></line>
                        <line
                            className="svg-line"
                            x1="12"
                            y1="6"
                            x2="12"
                            y2="12"
                            stroke="#fff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeDasharray="6 6"
                            strokeDashoffset="6"
                        ></line>
                    </svg>
                    <span className="slider"></span>
                </label>
            </motion.div>
            <AnimatePresence mode="wait">
                {isConfirmModalOpen && (
                    <PowerConfirmModal client={client} onClose={() => setIsConfirmModalOpen(false)} />
                )}
            </AnimatePresence>
        </>
    );
};

const PowerConfirmModal = ({ client, onClose }) => {
    const { t } = useTranslation("devices");
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const deviceStatePower = useAppStore(state => state.devicesState[client]?.power)
    const toggleDevicePower = useAppStore(state => state.toggleDevicePower)
    const modalContainerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalContainerRef.current && modalContainerRef.current.contains(e.target)) {
                return;
            }
            onClose();
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [onClose]);

    const handlePowerToggle = async () => {
        try {
            const newPowerState = deviceStatePower === "ON" ? "OFF" : "ON";
            await toggleDevicePower(client, newPowerState);
            onClose(); // Zatvori modal nakon uspešnog odgovora
        } catch (error) {
            console.error('Error toggling device power:', error);
            // Modal ostaje otvoren u slučaju greške
        }
    }

    const modalContent = (
        <>
            <motion.div
                className="dv-edit-modal-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, delay: 0.14 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'none' }}
            >
                <motion.div
                    ref={modalContainerRef}
                    className={`dv-power-confirm-modal ${isDarkTheme ? "dark-dv-power-confirm-modal" : "white-dv-power-confirm-modal"}`}
                    initial={{ opacity: 0, scale: 0.4 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.4 }}
                    transition={{ duration: 0.14, ease: "easeInOut" }}
                    style={{ pointerEvents: 'auto' }}
                >

                    <button
                        className={`dv-edit-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                        onClick={onClose}
                        aria-label={t("powerConfirm.closeAria")}
                    >
                        <svg
                            width="28"
                            height="28"
                            viewBox="0 0 28 28"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ display: "block", margin: "auto" }}
                        >
                            <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                            <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                        </svg>
                    </button>
                    <h3>{t("powerConfirm.title")}</h3>
                    <p>{t("powerConfirm.messageBefore")}<b>{deviceStatePower === "ON" ? t("powerConfirm.turnOff") : t("powerConfirm.turnOn")}</b>{t("powerConfirm.messageAfter")}</p>
                    <div className="dv-power-confirm-modal-buttons">
                        <button className={`dv-power-confirm-modal-button confirm-btn ${isDarkTheme ? "dark-big-btn" : ""}`} onClick={handlePowerToggle}>{t("powerConfirm.confirm")}</button>
                    </div>
                </motion.div>
            </motion.div>
            <motion.div
                className={`profile-modal-bg-wrapper ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 1 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'auto' }}
            >
            </motion.div>
        </>
    );

    return createPortal(modalContent, document.body);
}

const EmployeeConnected = ({ employeeInfo }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    return (
        <div className="dv-card-employee-wrapper">
            <img
                className="dv-card-employee-img"
                src={employeeInfo.imageUrl}
                alt=""
                crossOrigin="use-credentials"
                onError={(e) => {
                    e.target.onerror = null; // sprečava loop
                    e.target.classList.add("dv-card-img-error"); // doda klasu
                    e.target.src = "/views/login.svg"; // fallback slika
                }}
            />
        </div>
    )
}

export default DevicesCard;

const WifiFull = ({ client }) => {
    const deviceState = useAppStore(state => state.devicesState[client]);

    const paths = [
        "M37.12,34.85c-.39,0-.78-.15-1.07-.45-1.57-1.6-3.67-2.5-5.91-2.52-2.23-.04-4.36.83-5.96,2.4-.59.58-1.54.57-2.12-.02-.58-.59-.57-1.54.02-2.12,2.15-2.1,4.97-3.26,7.97-3.26.04,0,.08,0,.12,0,3.04.03,5.89,1.25,8.02,3.42.58.59.57,1.54-.02,2.12-.29.29-.67.43-1.05.43Z",
        "M43.18,30.8c-.39,0-.78-.15-1.07-.45-3.21-3.27-7.49-5.1-12.07-5.15-.06,0-.12,0-.18,0-4.51,0-8.76,1.74-11.99,4.9-.59.58-1.54.57-2.12-.02s-.57-1.54.02-2.12c3.79-3.72,8.79-5.76,14.09-5.76.07,0,.14,0,.21,0,5.38.05,10.41,2.2,14.18,6.05.58.59.57,1.54-.02,2.12-.29.29-.67.43-1.05.43Z",
        "M47.81,25.71c-.39,0-.78-.15-1.07-.45-4.43-4.52-10.36-7.05-16.68-7.12-6.31-.1-12.3,2.34-16.82,6.77-.59.58-1.54.57-2.12-.02-.58-.59-.57-1.54.02-2.12,5.1-4.99,11.82-7.73,18.95-7.63,7.12.07,13.8,2.92,18.79,8.02.58.59.57,1.54-.02,2.12-.29.29-.67.43-1.05.43Z"
    ];

    const wifiColor = useMemo(() => {
        switch (deviceState.status) {
            case 'waiting': return '#DBBC66';
            case 'online': return '#2ca82cff';
            default: return '#0f0f1a';
        }
    }, [deviceState.status]);

    const visibleBars = useMemo(() => {
        if (deviceState.status !== 'online' || !deviceState.rssi) return 3;

        const rssi = deviceState.rssi;
        if (rssi >= -50) return 3;
        if (rssi >= -70) return 2;
        if (rssi >= -85) return 1;
        return 0;
    }, [deviceState.status, deviceState.rssi]);

    return (
        <AnimatePresence mode="wait">
            {deviceState.status === 'offline' ? (
                <motion.div
                    key="wifi-offline"

                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3 }}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 60 60"
                        width="34"
                        height="34"
                    >
                        <path
                            d="M37.12,34.85c-.39,0-.78-.15-1.07-.45-1.57-1.6-3.67-2.5-5.91-2.52-2.23-.04-4.36.83-5.96,2.4-.59.58-1.54.57-2.12-.02-.58-.59-.57-1.54.02-2.12,2.15-2.1,4.97-3.26,7.97-3.26.04,0,.08,0,.12,0,3.04.03,5.89,1.25,8.02,3.42.58.59.57,1.54-.02,2.12-.29.29-.67.43-1.05.43Z"
                            fill="#a10000"
                        />
                        <path
                            d="M43.18,30.8c-.39,0-.78-.15-1.07-.45-3.21-3.27-7.49-5.1-12.07-5.15-.06,0-.12,0-.18,0-4.51,0-8.76,1.74-11.99,4.9-.59.58-1.54.57-2.12-.02s-.57-1.54.02-2.12c3.79-3.72,8.79-5.76,14.09-5.76.07,0,.14,0,.21,0,5.38.05,10.41,2.2,14.18,6.05.58.59.57,1.54-.02,2.12-.29.29-.67.43-1.05.43Z"
                            fill="#a10000"
                        />
                        <path
                            d="M47.81,25.71c-.39,0-.78-.15-1.07-.45-4.43-4.52-10.36-7.05-16.68-7.12-6.31-.1-12.3,2.34-16.82,6.77-.59.58-1.54.57-2.12-.02-.58-.59-.57-1.54.02-2.12,5.1-4.99,11.82-7.73,18.95-7.63,7.12.07,13.8,2.92,18.79,8.02.58.59.57,1.54-.02,2.12-.29.29-.67.43-1.05.43Z"
                            fill="#a10000"
                        />
                        <circle
                            cx="30"
                            cy="39.85"
                            r="3.51"
                            fill="#a10000"
                        />
                        <line x1="14" y1="14" x2="40" y2="40" stroke="#a10000" strokeWidth="4" strokeLinecap="round" />
                    </svg>

                </motion.div>
            ) : (
                <motion.svg
                    key="wifi-icon"
                    viewBox="0 0 60 60"
                    width="34"
                    height="34"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {paths.map((d, i) => (
                        <motion.path
                            key={i}
                            d={d}
                            fill={wifiColor}
                            initial={{ opacity: 0 }}
                            animate={{
                                opacity: deviceState.status === "waiting" ? [0, 1, 0] : 1
                            }}
                            transition={{
                                duration: deviceState.status === "waiting" ? 1.5 : 0.3,
                                repeat: deviceState.status === "waiting" ? Infinity : 0,
                                delay: i * 0.3,
                            }}
                        />
                    ))}
                    <motion.circle
                        cx="30"
                        cy="39.85"
                        r="3.51"
                        fill={wifiColor}
                        initial={{ opacity: 0 }}
                        animate={{
                            opacity: deviceState.status === "waiting" ? [0, 1, 0] : 1
                        }}
                        transition={{
                            duration: deviceState.status === "waiting" ? 1.5 : 0.3,
                            repeat: deviceState.status === "waiting" ? Infinity : 0,
                            delay: 0
                        }}
                    />
                </motion.svg>
            )}
        </AnimatePresence>
    );
};

const DeviceStatusInfo = ({ client, type }) => {
    const { t } = useTranslation("devices");
    const deviceState = useAppStore(state => state.devicesState[client])

    const deviceStatus = useMemo(() => {
        if (deviceState.status === 'online') {
            if (deviceState.power) return deviceState.power
            return t("card.available")
        } else if (deviceState.status === 'offline') {
            return t("card.unavailable")
        }
    }, [deviceState, t])

    if (deviceState.status === "online") {
        return <OnlineStatus deviceStatus={deviceStatus} lastPing={deviceState.lastPing} />
    }

    if (deviceState.status === 'offline') {
        return <OfflineStatus lastPing={deviceState.lastPing} />
    }

    return <WaitingStatus lastPing={deviceState.lastPing} />
}

const OnlineStatus = ({ deviceStatus, lastPing }) => {
    const { t } = useTranslation("devices");

    return (
        <>
            <div>
                <span className="dv-label">{t("card.status")}</span>
                {/* <span className="dv-value">{deviceStatus}</span> */}
                <AnimatePresence mode="wait">
                    <motion.span
                        key={lastPing}
                        className="dv-value"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{ color: '#2ca82cff' }}
                        transition={{ duration: 0.24, ease: "easeInOut" }}
                    >
                        {deviceStatus}
                    </motion.span>
                </AnimatePresence>
            </div>
            <div>
                <span className="dv-label">{t("card.updated")}</span>
                <AnimatePresence mode="wait">
                    <motion.span
                        key={lastPing}
                        className="dv-value"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.24, ease: "easeInOut" }}
                        style={{ color: '#2ca82cff' }}
                    >
                        {lastPing}
                    </motion.span>
                </AnimatePresence>
            </div>
        </>
    );
};

const OfflineStatus = ({ lastPing }) => {
    const { t } = useTranslation("devices");

    return (
        <>
            <div>
                <span className="dv-label">{t("card.status")}</span>
                <span className="dv-value" style={{ color: "#a10000ff" }}>{t("card.unavailable")}</span>
            </div>
            <div>
                <span className="dv-label">{t("card.updated")}</span>
                <span className="dv-value" style={{ color: "#a10000ff" }}>
                    {lastPing ? lastPing : "----"}
                </span>
            </div>
        </>
    );
};

const WaitingStatus = ({ lastPing }) => {
    const { t } = useTranslation("devices");

    return (
        <>
            {/* Status */}
            <div className="flex gap-2 items-center">
                <span className="dv-label">{t("card.status")}</span>
                <motion.span
                    className="dv-value flex"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [1, 0, 1, 1, 0, 1] }}
                    style={{ color: "#DBBC66" }}
                    transition={{
                        duration: 2.4,
                        delay: 1.2,
                        repeat: Infinity,
                        repeatType: "loop",
                    }}
                >
                    {t("card.readingStatus")}
                    {[...Array(3)].map((_, i) => (
                        <motion.span
                            key={i}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0, 1, 0] }}
                            transition={{
                                duration: 1,
                                delay: i * 0.4,
                                repeat: Infinity,
                                repeatType: "loop",
                            }}
                        >
                            .
                        </motion.span>
                    ))}
                </motion.span>
            </div>

            {/* Last ping */}
            <div className="flex gap-2 items-center">
                <span className="dv-label">{t("card.updated")}</span>
                {lastPing ? (
                    <motion.span
                        className="dv-value"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 1, 1, 0] }}
                        style={{ color: "#DBBC66" }}
                        transition={{
                            duration: 4,
                            repeat: Infinity,
                            repeatType: "loop",
                        }}
                    >
                        {lastPing}
                    </motion.span>
                ) : (
                    <span className="dv-value flex">
                        {[...Array(3)].map((_, i) => (
                            <motion.span
                                key={i}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: [0, 1, 0] }}
                                style={{ color: "#DBBC66" }}
                                transition={{
                                    duration: 1,
                                    delay: i * 0.4,
                                    repeat: Infinity,
                                    repeatType: "loop",
                                }}
                            >
                                -
                            </motion.span>
                        ))}
                    </span>
                )}
            </div>
        </>
    );
};

